import { useState, type ReactNode } from "react";
import Info from "@mui/icons-material/Info";
import Warning from "@mui/icons-material/WarningAmberOutlined";
import { Box, IconButton, Link, Typography, useTheme } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type {
  MHAStatusType,
  PatientStateEvent,
  PatientStateWithContext,
  ResolvedSection136PlaceOfSafety,
  UserDetails,
} from "@thalamos/aspire-common";
import {
  DischargeReason,
  getActiveMhaStatus,
  getBaseFormTemplate,
  getFullFormName,
  MhaStatus,
} from "@thalamos/aspire-common";

import { Button } from "../../design-system/Button/Button.js";
import { LightTooltip } from "../../design-system/LightTooltip.js";
import {
  Banner,
  BannerList,
  PopupDialog,
} from "~/components/design-system/index.js";
import { formatMhaStatus } from "~/components/util.js";
import { ukLocalFormatDateTime } from "~/util.js";

export type PatientStateDisplayProps = {
  state: PatientStateWithContext;
  disableEditMHAStatusBtn?: boolean;
  showEditMhaStatus?: boolean;
  onEditMhaStatus?: () => void;
  // When true, the card fills its container width and the info-box row wraps
  // instead of sizing to content. Used by the dual-status layout so two cards
  // can be equal width and use the available space. Single-status view leaves
  // this off and keeps the original fit-content sizing.
  fullWidth?: boolean;
  // When true, suppress the internal "What is MHA Status?" link. The dual-status
  // layout renders it beside the card heading instead (DEV-2269).
  hideMhaStatusInfoLink?: boolean;
  // The Section 136 place of safety resolved via the priority hierarchy
  // (`resolveSection136PlaceOfSafety`), fetched by the caller. `undefined`
  // when the viewing org doesn't display MHA status (`patientStateEnabled`), or
  // when `state` isn't a live Section 136 — in either case the place-of-safety
  // box falls back to reading directly off `state.event`, as it always has.
  resolvedSection136PlaceOfSafety?: ResolvedSection136PlaceOfSafety;
};

type ActiveStateType = {
  state: MhaStatus;
  type: MHAStatusType;
};

const isSection3PendingRenewal = (state: ActiveStateType): boolean => {
  return state.state === MhaStatus.Section3 && state.type === "pending-renewal";
};

function isSection3PendingCto(state: ActiveStateType): boolean {
  return state.state === MhaStatus.Section3 && state.type === "pending-cto";
}

function isSection17APendingRenewalCto(state: ActiveStateType): boolean {
  return (
    state.state === MhaStatus.Section17A && state.type === "pending-renewal"
  );
}

function getFullFormNameBasedOnReasonForChange(
  reason: PatientStateEvent | undefined,
): string | undefined {
  if (reason && "formTemplateId" in reason && reason.formTemplateId) {
    const template = getBaseFormTemplate(reason.formTemplateId);
    const formName = getFullFormName(template);
    return formName;
  }
  return undefined;
}

/** The hover copy attributing a hand-entered value to whoever entered it. */
const manuallyInputByText = (
  updatedBy: UserDetails,
  effectiveDateTime: string,
): string =>
  `Manually input by ${updatedBy.name}${updatedBy.teamName ? ` (${updatedBy.teamName})` : ""} at ${formatDateTime(effectiveDateTime)}`;

const getUpdatedByUserText = (state: PatientStateWithContext): string => {
  const event = state.event;

  if (event && "updatedBy" in event && event.updatedBy) {
    return manuallyInputByText(event.updatedBy, event.effectiveDateTime);
  }
  return "";
};

const getTooltipForState = (
  state: PatientStateWithContext,
): { title: string; subtitle: string }[] => {
  const mhaStatus = state.state.mhaStatus.status;
  const relevantStatuses = [
    MhaStatus.Section5_4,
    MhaStatus.Section5_2,
    MhaStatus.Section4,
    MhaStatus.Section2,
    MhaStatus.Section3,
    MhaStatus.Section17A,
    MhaStatus.Section136,
  ];

  const statusReason = state.statusReason;
  const statusReasonIsManualEvent = statusReason?.type === "manualEvent";

  if (mhaStatus === MhaStatus.Unknown) {
    return [
      {
        title: ``,
        subtitle: `No information available`,
      },
    ];
  }

  const activeState = getActiveMhaStatus(state.state, dayjs().toISOString());

  if (activeState) {
    if (isSection3PendingRenewal(activeState)) {
      return [
        {
          title: `A Section 3 renewal has already been completed for this patient. This will come into effect on ${formatDateTime(state.state.mhaStatus.startDateTime)}`,
          subtitle: "",
        },
      ];
    }

    if (isSection3PendingCto(activeState)) {
      return [
        {
          title: `A CTO has already been completed for this patient. This will come into effect on ${formatDateTime(state.state.mhaStatus.startDateTime)}`,
          subtitle: "",
        },
      ];
    }

    if (isSection17APendingRenewalCto(activeState)) {
      return [
        {
          title: `A CTO extension has already been completed for this patient. This will come into effect on ${formatDateTime(state.state.mhaStatus.startDateTime)}`,
          subtitle: "",
        },
      ];
    }
  }

  if (mhaStatus === MhaStatus.Incomplete) {
    return [
      {
        title: `${formatMhaStatus(mhaStatus)} status`,
        subtitle: `Data is missing, please contact your local MHA Legal Team to make sure the status is accurate.`,
      },
    ];
  }

  const isAutomaticExpiry = getIsAutomaticExpiry(state);

  if (isAutomaticExpiry) {
    if (state.event && "previousState" in state.event) {
      return [
        {
          title: `${formatMhaStatus(state.event.previousState.mhaStatus.status)} expired at ${formatDateTime(state.effectiveDateTime)}`,
          subtitle: `Patient detention period has expired, see patient log for more detail. Please contact your local MHA Legal Team to make sure the status is accurate.`,
        },
      ];
    }
    return [];
  }

  if (mhaStatus === MhaStatus.NotDetained) {
    if (statusReasonIsManualEvent) {
      const dischargeReason = state.state.episodeOfCare?.dischargeReason;

      const updatedByUserText = getUpdatedByUserText(state);

      if (dischargeReason === DischargeReason.Section23) {
        return [
          {
            title: `Discharged ${DischargeReason.Section23}`,
            subtitle: updatedByUserText,
          },
        ];
      }

      if (dischargeReason === DischargeReason.HoldingPowersDischarge) {
        return [
          {
            title: `Discharged from holding power`,
            subtitle: updatedByUserText,
          },
        ];
      }

      if (dischargeReason === DischargeReason.TribunalOrNearestRelative) {
        return [
          {
            title: `${DischargeReason.TribunalOrNearestRelative}`,
            subtitle: updatedByUserText,
          },
        ];
      }

      if (dischargeReason === DischargeReason.Section136Outcome) {
        return [
          {
            title: "Discharged from Section 136",
            subtitle: updatedByUserText,
          },
        ];
      }

      if (dischargeReason === DischargeReason.Other) {
        return [
          {
            title: "Discharged, other reason",
            subtitle: updatedByUserText,
          },
        ];
      }
    } else {
      const dischargeReason = state.state.episodeOfCare?.dischargeReason;
      if (dischargeReason === DischargeReason.Section23) {
        return [
          {
            title: `Discharged ${DischargeReason.Section23}`,
            subtitle: `A ${DischargeReason.Section23} discharge form has been signed for this patient`,
          },
        ];
      }
      if (dischargeReason === DischargeReason.HoldingPowersDischarge) {
        return [
          {
            title: `Holding Powers Discharge`,
            subtitle: `A discharge form has been signed for this patient`,
          },
        ];
      }
      if (dischargeReason === DischargeReason.Section136Outcome) {
        return [
          {
            title: `Discharged following Section 136 outcome`,
            subtitle: `A Section 136 outcome form has been completed for this patient`,
          },
        ];
      }
    }
  }

  if (relevantStatuses.includes(mhaStatus) && statusReasonIsManualEvent) {
    return [
      {
        title: `MHA Status ${formatMhaStatus(mhaStatus)}`,
        subtitle: `Manually input by ${statusReason.updatedBy.name}${statusReason.updatedBy.teamName ? ` (${statusReason.updatedBy.teamName})` : ""} at ${formatDateTime(statusReason.effectiveDateTime)}`,
      },
    ];
  } else if (relevantStatuses.includes(mhaStatus)) {
    return getSignedByTooltip(statusReason, mhaStatus);
  }
  return [];
};

const getSignedByTooltip = (
  statusReason: PatientStateEvent | undefined,
  mhaStatus: MhaStatus,
): { title: string; subtitle: string }[] => {
  const formName = getFullFormNameBasedOnReasonForChange(statusReason);

  let subtitle = ``;
  if (
    statusReason &&
    "signedBy" in statusReason &&
    statusReason.signedBy?.name &&
    statusReason.signedBy?.teamName
  ) {
    subtitle = `Signed by ${statusReason.signedBy?.name} (${statusReason.signedBy?.teamName})`;
  }
  return [
    {
      title: `${formName ?? formatMhaStatus(mhaStatus)}`,
      subtitle: subtitle,
    },
  ];
};

const getIsAutomaticExpiry = (state: PatientStateWithContext): boolean => {
  if (
    state.state.mhaStatus.status !== MhaStatus.Expired ||
    state.event?.type !== "automaticExpiry"
  ) {
    return false;
  }
  // Section 136 has no hospital — its location is a place of safety — so accept
  // a previous Section 136 detention as well as any hospital-based detention.
  return (
    !!state.event.previousState.hospital ||
    state.event.previousState.mhaStatus.status === MhaStatus.Section136
  );
};

// True when this is the automatic expiry of a Section 136 detention. The
// expired row's status is `Expired`; the previous status is read from the
// expiry event. Used to switch the hospital box over to place-of-safety copy.
const isExpiredSection136 = (state: PatientStateWithContext): boolean =>
  state.state.mhaStatus.status === MhaStatus.Expired &&
  state.event?.type === "automaticExpiry" &&
  state.event.previousState.mhaStatus.status === MhaStatus.Section136;

// True when this row is a Section 136 that was ended by completing the outcome
// (monitoring) form. Like an expired 136 it has no hospital — the location is a
// place of safety carried on the event — so the hospital box shows POS copy.
// Exported so the banner can decide to still resolve the place of safety after
// the 136 has ended (the resolved hover copy should show on the ended row too).
export const isEndedSection136ViaOutcome = (
  state: PatientStateWithContext,
): boolean => state.event?.type === "s136Outcome";

/**
 * The place of safety carried onto this row's event by whatever ended a Section
 * 136 (see `SupersededSection136PlaceOfSafety`).
 */
const supersededPlaceOfSafetyName = (
  state: PatientStateWithContext,
): string | undefined =>
  state.event && "previousPlaceOfSafety" in state.event
    ? state.event.previousPlaceOfSafety?.name
    : undefined;

/**
 * Who hand-entered the place of safety carried onto this row's event, if anyone
 * — absent when it was populated from a form.
 */
const supersededPlaceOfSafetyManualEntry = (
  state: PatientStateWithContext,
): { updatedBy: UserDetails; effectiveDateTime: string } | undefined =>
  state.event && "previousPlaceOfSafetyManualEntry" in state.event
    ? state.event.previousPlaceOfSafetyManualEntry
    : undefined;

/** The hospital named by the last event that set one, if any. */
const lastSetHospitalName = (
  state: PatientStateWithContext,
): string | undefined =>
  (state.hospitalReason as { hospital?: { name?: string } } | undefined)
    ?.hospital?.name;

/**
 * Whether this row is a Section 136 discharge entered by hand. It records the
 * place of safety in place of a hospital, so that place of safety outranks any
 * hospital carried over from an earlier row.
 */
const isManualSection136Discharge = (state: PatientStateWithContext): boolean =>
  state.event?.type === "manualEvent" &&
  state.event.previousPlaceOfSafety !== undefined;

/**
 * Whether the hospital box is showing the place of safety of a Section 136 that
 * a signed form or a manual discharge ended, rather than a hospital — so the box
 * must be labelled as a place of safety. Mirrors the precedence in
 * {@link getHospitalSubtitle}: a hospital named for this patient wins, except
 * over a manual Section 136 discharge.
 */
const showsSupersededPlaceOfSafety = (
  state: PatientStateWithContext,
): boolean =>
  supersededPlaceOfSafetyName(state) !== undefined &&
  (isManualSection136Discharge(state) || !lastSetHospitalName(state));

/**
 * Whether the banner should resolve (fetch + display) the Section 136 place of
 * safety for this patient. True only when the org displays MHA status
 * (`patientStateEnabled`) AND the patient has a 136 whose resolved
 * place-of-safety hover is shown:
 *   - a live Section 136, or
 *   - a 136 that ended via the outcome form (its source hover still shows).
 * Every other case (no 136, an automatic expiry, a discharge by other means)
 * uses the existing non-resolved display, so there is nothing to resolve and the
 * extra request is skipped. Extracted and exported so this "does the hover
 * appear at all" decision — the seam where display bugs have hidden — is unit
 * tested across the full (flag × status) matrix rather than left inline.
 */
export const shouldResolveSection136PlaceOfSafety = (
  mhaStatusDisplayEnabled: boolean,
  states: PatientStateWithContext[] | undefined,
): boolean =>
  mhaStatusDisplayEnabled &&
  (states?.some(
    (s) =>
      s.state.mhaStatus.status === MhaStatus.Section136 ||
      isEndedSection136ViaOutcome(s),
  ) ??
    false);

const getHospitalSubtitle = (
  state: PatientStateWithContext,
  resolvedSection136PlaceOfSafety?: ResolvedSection136PlaceOfSafety,
): string => {
  // Section 136: there is no hospital — POS lives in the event JSON. Prefer
  // the resolved priority-hierarchy winner when available; it reflects every
  // reported source, not just whichever event created this row.
  if (state.state.mhaStatus.status === MhaStatus.Section136) {
    if (resolvedSection136PlaceOfSafety) {
      return resolvedSection136PlaceOfSafety.placeOfSafety.name || "Unknown";
    }
    const ev = state.event as
      | {
          placeOfSafety?: { name?: string };
          section136Context?: { placeOfSafety?: { name?: string } };
        }
      | undefined;
    const posName =
      ev?.placeOfSafety?.name ?? ev?.section136Context?.placeOfSafety?.name;
    return posName || "Unknown";
  }

  // Section 136 ended via the outcome form: no hospital — show the place of
  // safety. Prefer the resolved priority-hierarchy winner (so it matches the
  // hover copy), falling back to the POS carried onto the outcome event.
  if (state.event?.type === "s136Outcome") {
    return (
      resolvedSection136PlaceOfSafety?.placeOfSafety.name ??
      state.event.placeOfSafety?.name ??
      "Unknown"
    );
  }

  const isAutomaticExpiry = getIsAutomaticExpiry(state);
  if (isAutomaticExpiry && state.event && "previousState" in state.event) {
    // Expired Section 136: no hospital — show the place of safety carried onto
    // the expiry event instead.
    if (isExpiredSection136(state)) {
      return state.event.previousPlaceOfSafety?.name || "Unknown";
    }
    return state.event.previousState.hospital?.name || "";
  }

  const hospitalName = lastSetHospitalName(state);
  if (hospitalName && !isManualSection136Discharge(state)) {
    return hospitalName;
  }

  return supersededPlaceOfSafetyName(state) ?? "Unknown";
};

const getTooltipForHospital = (
  state: PatientStateWithContext,
  t: TFunction,
  resolvedSection136PlaceOfSafety?: ResolvedSection136PlaceOfSafety,
): { title: string; subtitle: string }[] | undefined => {
  const { status } = state.state.mhaStatus;
  // A live Section 136 has no automatic-expiry/manual-event tooltip branch
  // below to fall into — its hover copy comes entirely from the resolved
  // priority-hierarchy winner. The same resolved copy is shown on a 136 that
  // has ended via the outcome form (per Product: ended statuses still show
  // their source hover), which is the only way the "populated from Outcome
  // Form" source is ever surfaced.
  if (
    (status === MhaStatus.Section136 || isEndedSection136ViaOutcome(state)) &&
    resolvedSection136PlaceOfSafety
  ) {
    return [
      {
        title: t("pages.editMHAStatus.placeOfSafety"),
        subtitle: resolvedSection136PlaceOfSafety.hoverText,
      },
    ];
  }
  const isAutomaticExpiry = getIsAutomaticExpiry(state);
  if (isAutomaticExpiry) {
    const stateEvent = state.event;
    const autoExpiryFormName =
      getFullFormNameBasedOnReasonForChange(stateEvent);

    const isPreviousEventManual =
      stateEvent &&
      "previousType" in stateEvent &&
      stateEvent.previousType === "manualEvent";

    if (isExpiredSection136(state)) {
      return [
        {
          title: t("pages.editMHAStatus.placeOfSafety"),
          subtitle: isPreviousEventManual
            ? getUpdatedByUserText(state)
            : t("pages.editMHAStatus.placeOfSafetyPopulatedFromForm"),
        },
      ];
    }

    const formName = autoExpiryFormName
      ? autoExpiryFormName
      : state.event && "previousState" in state.event
        ? state.event.previousState.mhaStatus.status
        : "";
    return [
      {
        title: `Hospital`,
        subtitle: isPreviousEventManual
          ? getUpdatedByUserText(state)
          : formName
            ? `Hospital name populated from ${formName}`
            : ``,
      },
    ];
  }
  // Whatever ended a Section 136, a signed form or a manual discharge, shows the
  // same place-of-safety hover as an expired 136 does, distinguishing the same two
  // provenances: the form the place of safety was recorded on, or the person who
  // entered it by hand.
  if (showsSupersededPlaceOfSafety(state)) {
    const manualEntry = supersededPlaceOfSafetyManualEntry(state);
    return [
      {
        title: t("pages.editMHAStatus.placeOfSafety"),
        subtitle: manualEntry
          ? manuallyInputByText(
              manualEntry.updatedBy,
              manualEntry.effectiveDateTime,
            )
          : t("pages.editMHAStatus.placeOfSafetyPopulatedFromForm"),
      },
    ];
  }

  const hospitalReason: any = state.hospitalReason;
  const hospitalReasonIsManualEvent = hospitalReason?.type === "manualEvent";

  if (hospitalReasonIsManualEvent) {
    return [
      {
        title: `Hospital`,
        subtitle: `Manually input by ${hospitalReason.updatedBy.name}${hospitalReason.updatedBy.teamName ? ` (${hospitalReason.updatedBy.teamName})` : ""} at ${formatDateTime(hospitalReason.effectiveDateTime)}`,
      },
    ];
  } else if (hospitalReason && hospitalReason.hospital?.name) {
    const formName = getFullFormNameBasedOnReasonForChange(hospitalReason);
    return [
      {
        title: `Hospital`,
        subtitle: `Hospital name populated from ${formName ?? status}`,
      },
    ];
  }

  return undefined;
};

const getTooltipForManualExpiryOverride = (
  state: PatientStateWithContext,
): { title: string; subtitle: string }[] => {
  if (state.expiryOverrideInfo) {
    return [
      {
        title: "Expiry date/time was overridden",
        subtitle: `Expiry date/time before override: ${ukLocalFormatDateTime(state.expiryOverrideInfo.defaultExpiryDateTime)}`,
      },
      {
        title: "Expiry date/time reason",
        subtitle: state.expiryOverrideInfo.reason,
      },
      {
        title: "Expiry date/time reason description",
        subtitle: state.expiryOverrideInfo.reasonDescription,
      },
    ];
  }
  return [];
};

const getTooltipForExpiry = (
  state: PatientStateWithContext,
): { title: string; subtitle: string }[] => {
  const { status } = state.state.mhaStatus;

  const activeState = getActiveMhaStatus(state.state, dayjs().toISOString());

  if (activeState) {
    if (isSection3PendingRenewal(activeState)) {
      return [
        {
          title: "Original Section 3 start date:",
          subtitle: formatDateTime(state.state.mhaStatus.initialStartDateTime!),
        },
        {
          title: "Current Section 3 start date:",
          subtitle: formatDateTime(state.state.mhaStatus.startDateTime!),
        },
        {
          title: "Current Section 3 end date:",
          subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime!),
        },
      ];
    }
    if (isSection17APendingRenewalCto(activeState)) {
      return [
        {
          title: "Original CTO start date:",
          subtitle: formatDateTime(state.state.mhaStatus.initialStartDateTime!),
        },
        {
          title: "Current CTO start date:",
          subtitle: formatDateTime(state.state.mhaStatus.startDateTime!),
        },
        {
          title: "Current CTO end date:",
          subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime!),
        },
      ];
    }

    if (isSection3PendingCto(activeState)) {
      return [
        {
          title: "CTO start date:",
          subtitle: formatDateTime(state.state.mhaStatus.startDateTime!),
        },
        {
          title: "CTO end date:",
          subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime!),
        },
      ];
    }
  }

  if (
    status === MhaStatus.Section5_4 ||
    status === MhaStatus.Section5_2 ||
    status === MhaStatus.Section4 ||
    status === MhaStatus.Section2
  ) {
    return [
      {
        title: "Detention start date/time:",
        subtitle: formatDateTime(state.state.mhaStatus.startDateTime!),
      },
      {
        title: "Detention end date/time:",
        subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime!),
      },
    ];
  }

  if (status === MhaStatus.Section136) {
    // The expiry hover shows only the detention timing. Place-of-safety details
    // are deliberately NOT read here: they belong on the location box,
    // and reading POS from the event risked the hover inheriting POS from a
    // previous status. The displayed POS is unchanged — it stays on the location
    // box via getHospitalSubtitle. Police/officer context lives on the event
    // JSON, not the state row.
    const ev = state.event as
      | {
          type?: string;
          policeContext?: { officerName?: string; forceName?: string };
          section136Context?: {
            policeContext?: { officerName?: string; forceName?: string };
          };
        }
      | undefined;
    const police = ev?.policeContext ?? ev?.section136Context?.policeContext;
    const rows: { title: string; subtitle: string }[] = [
      {
        title: "Arrival at place of safety:",
        subtitle: formatDateTime(state.state.mhaStatus.startDateTime),
      },
      {
        title: "Section 136 expires:",
        subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime),
      },
    ];
    if (police?.officerName) {
      rows.push({
        title: "Officer:",
        subtitle: police.forceName
          ? `${police.officerName} (${police.forceName})`
          : police.officerName,
      });
    }
    return rows;
  }

  if (status === MhaStatus.Section3 || status === MhaStatus.Section17A) {
    const hasRenewals = getHasRenewals(state);
    if (hasRenewals && state.state.mhaStatus.numberOfRenewals! > 0) {
      return [
        {
          title: "Original Detention/CTO start date/time:",
          subtitle: formatDateTime(state.state.mhaStatus.initialStartDateTime!),
        },
        {
          title: "Current Section/CTO start date:",
          subtitle: formatDateTime(state.state.mhaStatus.startDateTime!),
        },
        {
          title: "Current Section/CTO end date:",
          subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime!),
        },
      ];
    } else {
      return [
        {
          title: "Section/CTO start date:",
          subtitle: formatDateTime(state.state.mhaStatus.startDateTime!),
        },
        {
          title: "Section/CTO end date:",
          subtitle: formatDateTime(state.state.mhaStatus.expiryDateTime!),
        },
      ];
    }
  }

  if (status === MhaStatus.NotDetained) {
    if (state.event?.type === "manualEvent") {
      return [
        {
          title: "Expired",
          subtitle: getUpdatedByUserText(state),
        },
      ];
    } else {
      return [
        {
          title: "Expired",
          subtitle: `Date and time populated from relevant form ${status}`,
        },
      ];
    }
  }

  return [];
};

export function InfoBox(props: {
  title: string;
  subtitle: string;
  useSmallSubtitle?: boolean;
  tooltip?: { title: string; subtitle: string }[];
  showWarningIcon?: boolean;
  warningColor?: string;
  testId?: string;
  maxWidth?: string;
}) {
  const theme = useTheme();
  const tooltipTriggerTestId = props.testId
    ? `${props.testId}-tooltip-trigger`
    : undefined;
  const tooltipItems = (props.tooltip ?? []).filter(
    (item) => item.title || item.subtitle,
  );

  return (
    <Box
      data-testid={props.testId}
      sx={{
        display: "flex",
        flexDirection: "row",
        maxWidth: props.maxWidth ?? "250px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Typography
          sx={{ fontWeight: 400, fontSize: "11px", lineHeight: "13px" }}
        >
          {props.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              marginTop: "3px",
              fontWeight: 600,
              fontSize: props.useSmallSubtitle ? "11px" : "15px",
              lineHeight: props.useSmallSubtitle ? "13px" : "22px",
              color: theme.palette.common.blueGray,
              fontStyle: "normal",
            }}
          >
            {props.subtitle}
          </Typography>
        </Box>
      </Box>
      {tooltipItems.length > 0 && (
        <LightTooltip
          placement="top"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -14],
                  },
                },
              ],
            },
          }}
          title={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: theme.spacing(0.5),
              }}
            >
              {tooltipItems.map((item) => (
                <Box key={item.title}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "11px",
                      lineHeight: "13px",
                      color: theme.palette.common.blueGray,
                      fontStyle: "normal",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "11px",
                      lineHeight: "13px",
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                </Box>
              ))}
            </Box>
          }
        >
          {props.showWarningIcon ? (
            <Box
              data-testid={tooltipTriggerTestId}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Warning sx={{ color: props.warningColor, paddingLeft: "5px" }} />
            </Box>
          ) : (
            <IconButton
              data-testid={tooltipTriggerTestId}
              sx={{ padding: 0, paddingLeft: "5px" }}
              color="primary"
            >
              <Info />
            </IconButton>
          )}
        </LightTooltip>
      )}
    </Box>
  );
}

export function formatDateTime(dateTime?: string) {
  return dateTime && dayjs(dateTime).isValid()
    ? dayjs(dateTime).tz("Europe/London").format("DD.MM.YY - HH:mm")
    : "Unknown";
}

function zeroPad(num: number, places: number) {
  if (num === 0) return "0";
  const zero = places - num.toString().length + 1;
  return Array(+(zero > 0 && zero)).join("0") + num;
}

function getHasRenewals(state: PatientStateWithContext): boolean {
  return (
    state.state.mhaStatus &&
    [MhaStatus.Section17A, MhaStatus.Section3].includes(
      state.state.mhaStatus?.status,
    ) &&
    state.state.mhaStatus.numberOfRenewals !== undefined
  );
}

function MhaStatusDialog({
  closeDialog,
  content,
  buttonLabel,
  open,
}: {
  closeDialog: () => void;
  open: boolean;
  content: ReactNode;
  buttonLabel: string;
}) {
  const theme = useTheme();
  return (
    <PopupDialog open={open} onClose={closeDialog}>
      <Banner
        bannerType={BannerList.INFO}
        body={content}
        sx={{ marginBottom: theme.spacing(2) }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button variant="outlined" label={buttonLabel} onClick={closeDialog} />
      </Box>
    </PopupDialog>
  );
}

/**
 * The "What is MHA Status?" help link and its explanatory dialog. Extracted so
 * the dual-status layout can place it beside each card's heading rather than
 * inside the status body (DEV-2269).
 */
function DialogHeading({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ fontWeight: 600, color: "black" }}>{children}</Typography>
  );
}

export function WhatIsMhaStatusLink() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const dialogContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <DialogHeading>
        {t("pages.patientStateDisplay.whatIsMHAStatus")}
      </DialogHeading>
      <Typography>
        {t("pages.patientStateDisplay.mhaStatusExplained")}
      </Typography>
      <DialogHeading>
        {t("pages.patientStateDisplay.informationFrom")}
      </DialogHeading>
      <Typography>
        {t("pages.patientStateDisplay.informationFromExplained")}
      </Typography>
      <Typography>{t("pages.patientStateDisplay.contactLegalTeam")}</Typography>
    </Box>
  );

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Typography
          sx={{
            textDecoration: "none",
            backgroundColor: "transparent",
            outline: "none",
            color: "inherit",
            fontWeight: 400,
            fontSize: "9px",
            lineHeight: "13px",
          }}
          variant="subtitle1"
        >
          {t("pages.patientStateDisplay.whatIsMHAStatus")}
        </Typography>
      </Link>
      {open && (
        <MhaStatusDialog
          closeDialog={() => {
            setOpen(false);
          }}
          content={dialogContent}
          buttonLabel={t("buttonLabels.close")}
          open={open}
        />
      )}
    </>
  );
}

export function infoBoxMhaStatusTitle(
  state: PatientStateWithContext,
  activeState: ActiveStateType,
): string {
  const title = `${formatMhaStatus(state.state.mhaStatus.status).toUpperCase()}`;

  if (activeState) {
    if (isSection3PendingRenewal(activeState)) {
      return `${formatMhaStatus(activeState.state, activeState.type).toUpperCase()}`;
    }

    if (isSection3PendingCto(activeState)) {
      return `${formatMhaStatus(activeState.state, activeState.type).toUpperCase()}`;
    }

    if (isSection17APendingRenewalCto(activeState)) {
      return `${formatMhaStatus(activeState.state, activeState.type).toUpperCase()}`;
    }
  }

  return title;
}

export function PatientStateDisplay({
  state,
  disableEditMHAStatusBtn,
  showEditMhaStatus,
  onEditMhaStatus,
  fullWidth,
  hideMhaStatusInfoLink,
  resolvedSection136PlaceOfSafety,
}: PatientStateDisplayProps) {
  const mhaStatus = state.state.mhaStatus.status;

  const theme = useTheme();
  const { t } = useTranslation();

  const isAutomaticExpiry = getIsAutomaticExpiry(state);
  const isUnknown = mhaStatus === MhaStatus.Unknown;
  const isDischarged = mhaStatus === MhaStatus.NotDetained;
  const isUnknownExpired = mhaStatus === MhaStatus.Expired;
  const hasRenewals = getHasRenewals(state);
  const expiryManuallyOverridden = !!state.expiryOverrideInfo;

  const isExpiredSections = [MhaStatus.Expired, MhaStatus.NotDetained];

  const activeState = getActiveMhaStatus(state.state, dayjs().toISOString());

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: theme.spacing(0.3),
        columnGap: theme.spacing(3),
        alignItems: "stretch",
        backgroundColor: "white",
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        width: fullWidth ? "100%" : "fit-content",
        boxSizing: "border-box",
        fontStyle: "normal",
      }}
    >
      {!hideMhaStatusInfoLink && (
        <Box
          sx={{
            display: "flex",
            paddingBottom: theme.spacing(1),
            marginLeft: "auto",
          }}
        >
          <WhatIsMhaStatusLink />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          columnGap: theme.spacing(3),
          rowGap: fullWidth ? theme.spacing(1.5) : 0,
          flexWrap: fullWidth ? "wrap" : "nowrap",
          alignItems: "stretch",
          width: fullWidth ? "100%" : "fit-content",
          fontStyle: "normal",
        }}
      >
        <InfoBox
          title="MHA Status"
          subtitle={infoBoxMhaStatusTitle(state, activeState)}
          tooltip={getTooltipForState(state)}
          showWarningIcon={
            isAutomaticExpiry || mhaStatus === MhaStatus.Incomplete
          }
          warningColor={
            isAutomaticExpiry
              ? theme.palette.error.darker
              : theme.palette.warning.main
          }
          testId="mhaStatus"
        />
        {!isUnknown && (
          <>
            {isDischarged && (
              <>
                {state.state.episodeOfCare?.dischargeDateTime && (
                  <InfoBox
                    testId="episodeOfCareDischarge"
                    title="Expired"
                    subtitle={formatDateTime(
                      state.state.episodeOfCare.dischargeDateTime,
                    )}
                    tooltip={
                      state.event?.type === "manualEvent"
                        ? [
                            {
                              title: `Discharge date/time`,
                              subtitle: getUpdatedByUserText(state),
                            },
                          ]
                        : [
                            {
                              title: `Discharge date/time`,
                              subtitle: `Date and time populated from relevant form ${getFullFormNameBasedOnReasonForChange(state.dischargeReason) ?? state.state.mhaStatus.status}`,
                            },
                          ]
                    }
                  />
                )}
              </>
            )}
            {!isDischarged && (
              <>
                {/* A Section 136 ended via the outcome form as Incomplete
                    (admission / other) has no expiry date — showing an
                    "Expiry: Unknown" box would be misleading, so suppress it and
                    leave just the status chip and the place-of-safety box. The
                    Expired outcome keeps the recorded end time, so its box shows
                    "Expired: <time>" as normal. */}
                {!(
                  isEndedSection136ViaOutcome(state) &&
                  !state.state.mhaStatus.expiryDateTime
                ) && (
                  <InfoBox
                    testId="expiryDateTime"
                    showWarningIcon={expiryManuallyOverridden}
                    warningColor="orange"
                    title={
                      isExpiredSections.includes(state.state.mhaStatus.status)
                        ? "Expired"
                        : "Expiry"
                    }
                    subtitle={
                      isAutomaticExpiry
                        ? formatDateTime(
                            state.state.episodeOfCare!.dischargeDateTime!,
                          )
                        : formatDateTime(state.state.mhaStatus.expiryDateTime!)
                    }
                    tooltip={[
                      ...getTooltipForManualExpiryOverride(state),
                      ...getTooltipForExpiry(state),
                    ]}
                  />
                )}
                {hasRenewals && (
                  <InfoBox
                    testId="numberOfRenewals"
                    title={`${state.state.mhaStatus.status === MhaStatus.Section17A ? "Extensions" : "Renewals"}`}
                    subtitle={zeroPad(
                      state.state.mhaStatus.numberOfRenewals ?? 0,
                      2,
                    )}
                    tooltip={
                      state.event?.type === "manualEvent"
                        ? [
                            {
                              title: `Renewals`,
                              subtitle: getUpdatedByUserText(state),
                            },
                          ]
                        : undefined
                    }
                  />
                )}
              </>
            )}
            {!isUnknown && (
              <InfoBox
                testId="hospital"
                maxWidth={fullWidth ? "none" : undefined}
                title={
                  state.state.mhaStatus.status === MhaStatus.Section136
                    ? `${t("pages.editMHAStatus.placeOfSafety")}:`
                    : isExpiredSection136(state) ||
                        isEndedSection136ViaOutcome(state) ||
                        showsSupersededPlaceOfSafety(state)
                      ? `${t("pages.editMHAStatus.previousPlaceOfSafety")}:`
                      : isDischarged || isUnknownExpired
                        ? `${t("pages.editMHAStatus.previousHospital")}:`
                        : `${t("pages.editMHAStatus.hospital")}:`
                }
                subtitle={getHospitalSubtitle(
                  state,
                  resolvedSection136PlaceOfSafety,
                )}
                tooltip={getTooltipForHospital(
                  state,
                  t,
                  resolvedSection136PlaceOfSafety,
                )}
              />
            )}
          </>
        )}
        {showEditMhaStatus && onEditMhaStatus && (
          <Button
            label="Edit"
            onClick={onEditMhaStatus}
            disabled={disableEditMHAStatusBtn}
            testId="editMhaStatusButton"
          />
        )}
      </Box>
    </Box>
  );
}
